import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text, useDisclosure } from '@chakra-ui/react'
import type { InsuranceParamsDto } from 'BrokerTsLib/src/dtos'
import { Selection } from 'BrokerTsLib/src/enums'
import { useEffect, useMemo, useRef } from 'react'
import { useFormContext } from 'react-hook-form'

/** Upozornení (modal dialog), který se zobrazí, když cena vozidla je bez DPH a pojistník je zároveň neplátcem DPH.
 * Zobrazí se pouze po efektivní změně podmínek.
 * Při prvním průchodu (nové pojištění) místo upozornění automaticky nastaví pojistníka na PO + plátce DPH = ANO.
 */
export const PriceExcludeVatWarning = ({ isDisabled, isNewInsurance }: { isDisabled: boolean, isNewInsurance: boolean }) => {
    const { isOpen, onOpen, onClose } = useDisclosure()
    const { watch, setValue, getValues } = useFormContext<InsuranceParamsDto>()
    const priceIncludeVat = watch('vehicle.priceIncludeVat')
    const policyHolderIsVatPayer = watch('policyHolder.person.isVatPayer')
    const holderIsVatPayer = watch('policyHolder.holder.isVatPayer')
    const ownerIsVatPayer = watch('policyHolder.owner.isVatPayer')
    const ownerSameAs = watch('policyHolder.ownerSameAs')

    const effectiveIsVatPayer = useMemo(() => {
        if (ownerSameAs === Selection.PersonSameAsPolicyHolder)
            return policyHolderIsVatPayer

        if (ownerSameAs === Selection.PersonSameAsKeeper)
            return holderIsVatPayer

        if (!ownerSameAs)
            return ownerIsVatPayer

        return null
    }, [ownerSameAs, policyHolderIsVatPayer, holderIsVatPayer, ownerIsVatPayer])

    const lastIsVatPayer = useRef<boolean | null>(effectiveIsVatPayer ?? null)
    const lastPriceIncludeVat = useRef<boolean | null>(priceIncludeVat)
    const isFirstPass = useRef(isNewInsurance)

    useEffect(() => {
        const changed = lastIsVatPayer.current !== effectiveIsVatPayer || lastPriceIncludeVat.current !== priceIncludeVat
        if (changed) {
            lastIsVatPayer.current = effectiveIsVatPayer ?? null
            lastPriceIncludeVat.current = priceIncludeVat

            if (!priceIncludeVat && !effectiveIsVatPayer && !isDisabled) {
                if (isFirstPass.current) {
                    // První průchod: auto-set pojistník, nezobrazovat upozornění
                    // Typ osoby nastavíme na PO pouze pokud je aktuálně FO (výchozí hodnota)
                    const currentType = getValues('policyHolder.person.type')
                    if (currentType !== Selection.Company && currentType !== Selection.SelfEmployed) {
                        setValue('policyHolder.person.type', Selection.Company)
                    }
                    setValue('policyHolder.person.isVatPayer', true)
                    isFirstPass.current = false
                } else {
                    onOpen()
                }
            }
        }
    }, [priceIncludeVat, effectiveIsVatPayer, onOpen, isDisabled, setValue, getValues])

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Upozornění</ModalHeader>
                <ModalBody>
                    <Text>Je uvedena cena vozidla bez DPH a současně je vlastník neplátcem DPH. Jsou zadaná data správná?</Text>
                </ModalBody>
                <ModalFooter>
                    <Button onClick={onClose}>OK</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    )
}
